import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InventoryService } from '../../services/inventory.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

export interface AvgCostRevalData {
	stockid: string;
	description?: string;
	currentCost?: number;
}

interface StepRow {
	seq: number;
	trandate: string;
	reference: string;
	qty: number;
	price: number;
	prevqty: number;
	newqoh: number;
	runningAvg: number;
	done: boolean;
}

@Component({
	selector: 'app-avg-cost-reval',
	templateUrl: './avg-cost-reval.component.html',
	styleUrls: ['./avg-cost-reval.component.scss'],
})
export class AvgCostRevalComponent implements OnInit {
	loading = false;
	applying = false;
	applied = false;

	steps: StepRow[] = [];
	visibleSteps: StepRow[] = [];
	calculatedCost: number | null = null;
	errorMsg: string | null = null;

	private readonly stepDelayMs = 60;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: AvgCostRevalData,
		private dialogRef: MatDialogRef<AvgCostRevalComponent>,
		private inventoryService: InventoryService,
		private globalSearchService: GlobalSearchService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.recalculate();
	}

	recalculate(): void {
		this.loading = true;
		this.steps = [];
		this.visibleSteps = [];
		this.calculatedCost = null;
		this.errorMsg = null;
		this.applied = false;

		this.inventoryService.batchCostRecalcAvg(this.data.stockid).subscribe({
			next: (r: any) => {
				this.loading = false;
				if (r?.error) {
					this.errorMsg = r.error;
					this.cdr.markForCheck();
					return;
				}
				this.steps = (r?.steps ?? []).map((s: any, i: number) => ({ ...s, done: false }));
				this.calculatedCost = r?.calculatedCost ?? null;
				this.cdr.markForCheck();
				this.animateSteps();
			},
			error: () => {
				this.loading = false;
				this.errorMsg = 'Failed to load movements. Please try again.';
				this.cdr.markForCheck();
			},
		});
	}

	private async animateSteps(): Promise<void> {
		for (let i = 0; i < this.steps.length; i++) {
			await this.delay(this.stepDelayMs);
			this.steps[i].done = true;
			this.visibleSteps = this.steps.slice(0, i + 1);
			this.cdr.markForCheck();
		}
	}

	apply(): void {
		if (this.calculatedCost === null) return;
		this.applying = true;
		this.inventoryService.batchCostApplyCalcCost(this.data.stockid, this.calculatedCost).subscribe({
			next: (r: any) => {
				this.applying = false;
				this.applied = true;
				this.cdr.markForCheck();
				this.globalSearchService.showNotification(
					`Avg cost updated to ${this.calculatedCost?.toFixed(4)} for ${this.data.stockid}`,
					'success', 'bottom', 'left'
				);
			},
			error: () => {
				this.applying = false;
				this.globalSearchService.showNotification('Failed to apply cost. Please try again.', 'danger', 'bottom', 'left');
				this.cdr.markForCheck();
			},
		});
	}

	close(): void {
		this.dialogRef.close(this.applied ? { updated: true, cost: this.calculatedCost } : null);
	}

	private delay(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}
}
